import React, { useState, useMemo } from 'react';
import { X, Search, ArrowRight, Package, CheckCircle, AlertTriangle, Warehouse, Store, MapPin, User, Clock, ChevronRight, Minus, Plus } from 'lucide-react';
import { Button } from '../ui/Button';
import { Reservation, InventoryItem, LocationInfo, WorkerInfo } from '../../types';
import { WorkerSelector } from './WorkerSelector';

interface ColorSize {
  id: string;
  name: string;
}

interface ReceivedItemState {
  item_id: string;
  product_id: string;
  expected_quantity: number;
  received_quantity: number;
}

interface TransferReceiptModalProps {
  isOpen: boolean;
  onClose: () => void;
  pendingTransfers: Reservation[];
  onConfirmReceipt: (
    transferId: string,
    receivedItems: Array<{ item_id: string; product_id: string; received_quantity: number }>,
    receivingWorkerId: string
  ) => Promise<boolean>;
  processing: boolean;
  products: InventoryItem[];
  colors: ColorSize[];
  sizes: ColorSize[];
  locations: LocationInfo[];
}

export const TransferReceiptModal: React.FC<TransferReceiptModalProps> = ({
  isOpen,
  onClose,
  pendingTransfers,
  onConfirmReceipt,
  processing,
  products,
  colors,
  sizes,
  locations
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTransfer, setSelectedTransfer] = useState<Reservation | null>(null);
  const [receivedItems, setReceivedItems] = useState<ReceivedItemState[]>([]);
  const [receivingWorker, setReceivingWorker] = useState<WorkerInfo | null>(null);
  const [notes, setNotes] = useState('');
  const [receiptError, setReceiptError] = useState<string | null>(null);

  const filteredTransfers = useMemo(() => {
    if (!searchQuery.trim()) return pendingTransfers;
    const query = searchQuery.toLowerCase();
    return pendingTransfers.filter(t =>
      t.reservation_number.toLowerCase().includes(query) ||
      t.worker_name?.toLowerCase().includes(query)
    );
  }, [pendingTransfers, searchQuery]);

  if (!isOpen) return null;

  const handleSelectTransfer = (transfer: Reservation) => {
    setSelectedTransfer(transfer);
    const items = transfer.reservation_items?.map(item => ({
      item_id: item.id,
      product_id: item.product_id,
      expected_quantity: item.quantity,
      received_quantity: item.received_quantity || 0
    })) || [];
    setReceivedItems(items);
  };

  const handleBack = () => {
    setSelectedTransfer(null);
    setReceivedItems([]);
    setReceivingWorker(null);
    setNotes('');
    setReceiptError(null);
  };

  const updateReceivedQuantity = (itemId: string, quantity: number) => {
    setReceivedItems(prev => prev.map(item =>
      item.item_id === itemId
        ? { ...item, received_quantity: Math.max(0, Math.min(quantity, item.expected_quantity)) }
        : item
    ));
  };

  const setAllAsReceived = () => {
    setReceivedItems(prev => prev.map(item => ({
      ...item,
      received_quantity: item.expected_quantity
    })));
  };

  const handleConfirmReceipt = async () => {
    if (!selectedTransfer || !receivingWorker) return;
    setReceiptError(null);

    const itemsToSubmit = receivedItems.map(item => ({
      item_id: item.item_id,
      product_id: item.product_id,
      received_quantity: item.received_quantity
    }));

    const success = await onConfirmReceipt(
      selectedTransfer.id,
      itemsToSubmit,
      receivingWorker.id
    );

    if (success) {
      handleBack();
      onClose();
    } else {
      setReceiptError('Failed to confirm receipt. Check inventory availability and try again.');
    }
  };

  const getProductInfo = (productId: string) => products.find(p => p.id === productId);
  const getColorName = (colorId?: string) => colors.find(c => c.id === colorId)?.name || '-';
  const getSizeName = (sizeId?: string) => sizes.find(s => s.id === sizeId)?.name || '-';
  const getLocationName = (locationId?: string) => locations.find(l => l.id === locationId)?.name || 'Unknown';

  const getLocationIcon = (name?: string) => {
    if (!name) return MapPin;
    const lowerName = name.toLowerCase();
    if (lowerName.includes('warehouse') || lowerName.includes('wearhouse')) return Warehouse;
    if (lowerName.includes('shop') || lowerName.includes('store')) return Store;
    return MapPin;
  };

  const totalExpected = receivedItems.reduce((sum, item) => sum + item.expected_quantity, 0);
  const totalReceived = receivedItems.reduce((sum, item) => sum + item.received_quantity, 0);
  const isPartial = totalReceived > 0 && totalReceived < totalExpected;
  const isComplete = totalReceived === totalExpected && totalExpected > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-3xl w-full overflow-hidden max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            {selectedTransfer && (
              <button
                onClick={handleBack}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ChevronRight className="h-5 w-5 text-gray-500 rotate-180" />
              </button>
            )}
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {selectedTransfer ? `Receive Transfer ${selectedTransfer.reservation_number}` : 'Receive Transfers'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {!selectedTransfer ? (
          <>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by transfer number or worker..."
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {filteredTransfers.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="h-16 w-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                  <p className="text-gray-500 dark:text-gray-400 font-medium">No pending transfers</p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                    All transfers have been received
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredTransfers.map(transfer => {
                    const sourceLocation = locations.find(l => l.id === transfer.source_location_id);
                    const destLocation = locations.find(l => l.id === transfer.destination_location_id);
                    const SourceIcon = getLocationIcon(sourceLocation?.name);
                    const DestIcon = getLocationIcon(destLocation?.name);

                    return (
                      <button
                        key={transfer.id}
                        onClick={() => handleSelectTransfer(transfer)}
                        className="w-full p-4 bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 transition-all text-left"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-gray-100">
                              {transfer.reservation_number}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {transfer.reservation_items?.length || 0} items
                            </p>
                          </div>
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            transfer.status === 'partial_receipt'
                              ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                              : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                          }`}>
                            {transfer.status === 'partial_receipt' ? 'Partial' : 'Pending'}
                          </span>
                        </div>

                        <div className="flex items-center gap-2 mb-3">
                          <div className="flex items-center gap-1.5 px-2 py-1 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                            <SourceIcon className="h-3.5 w-3.5 text-teal-600 dark:text-teal-400" />
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                              {sourceLocation?.name || 'Unknown'}
                            </span>
                          </div>
                          <ArrowRight className="h-4 w-4 text-gray-400" />
                          <div className="flex items-center gap-1.5 px-2 py-1 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                            <DestIcon className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                              {destLocation?.name || 'Unknown'}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {transfer.worker_name || 'Unknown'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {new Date(transfer.reserved_at).toLocaleDateString()}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-4">
              <div className="flex items-center justify-center gap-4 mb-6 p-4 bg-gradient-to-r from-teal-50 to-blue-50 dark:from-teal-900/20 dark:to-blue-900/20 rounded-xl border border-teal-200 dark:border-teal-800">
                <div className="flex flex-col items-center">
                  <span className="text-xs text-gray-500 dark:text-gray-400 uppercase mb-1">From</span>
                  <span className="font-semibold text-gray-900 dark:text-gray-100">
                    {getLocationName(selectedTransfer.source_location_id)}
                  </span>
                </div>
                <ArrowRight className="h-6 w-6 text-teal-500" />
                <div className="flex flex-col items-center">
                  <span className="text-xs text-gray-500 dark:text-gray-400 uppercase mb-1">To</span>
                  <span className="font-semibold text-gray-900 dark:text-gray-100">
                    {getLocationName(selectedTransfer.destination_location_id)}
                  </span>
                </div>
              </div>

              <div className="mb-4">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  Receiving Worker *
                </label>
                <WorkerSelector
                  selectedWorkerId={receivingWorker?.id || null}
                  onSelect={setReceivingWorker}
                  placeholder="Select who is receiving..."
                  autoSelectCurrent
                />
              </div>

              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Items to Receive
                </h3>
                <button
                  onClick={setAllAsReceived}
                  className="text-xs font-medium text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300"
                >
                  Mark All Received
                </button>
              </div>

              <div className="space-y-2 mb-4">
                {receivedItems.map(item => {
                  const product = getProductInfo(item.product_id);
                  const isFullyReceived = item.received_quantity === item.expected_quantity;
                  const isPartiallyReceived = item.received_quantity > 0 && item.received_quantity < item.expected_quantity;

                  return (
                    <div
                      key={item.item_id}
                      className={`p-3 rounded-xl border transition-all ${
                        isFullyReceived
                          ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
                          : isPartiallyReceived
                          ? 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800'
                          : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                            {product?.name || 'Unknown Product'}
                          </p>
                          <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {product?.reference_code && (
                              <span>{product.reference_code}</span>
                            )}
                            {product?.size_id && (
                              <span className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded">
                                {getSizeName(product.size_id)}
                              </span>
                            )}
                            {product?.color_id && (
                              <span>{getColorName(product.color_id)}</span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="text-xs text-gray-500 dark:text-gray-400">Expected</p>
                            <p className="font-semibold text-gray-900 dark:text-gray-100">
                              {item.expected_quantity}
                            </p>
                          </div>

                          <div className="flex items-center gap-1 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-0.5">
                            <button
                              onClick={() => updateReceivedQuantity(item.item_id, item.received_quantity - 1)}
                              className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                            >
                              <Minus className="h-3.5 w-3.5" />
                            </button>
                            <input
                              type="number"
                              value={item.received_quantity}
                              onChange={(e) => updateReceivedQuantity(item.item_id, parseInt(e.target.value) || 0)}
                              className="w-12 text-center text-sm font-semibold bg-transparent border-none focus:ring-0 text-gray-900 dark:text-gray-100"
                              min="0"
                              max={item.expected_quantity}
                            />
                            <button
                              onClick={() => updateReceivedQuantity(item.item_id, item.received_quantity + 1)}
                              className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
                              disabled={item.received_quantity >= item.expected_quantity}
                            >
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                          </div>

                          {isFullyReceived && (
                            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                          )}
                          {isPartiallyReceived && (
                            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mb-4">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  Notes (optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes about discrepancies or issues..."
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                  rows={2}
                />
              </div>

              <div className={`p-4 rounded-xl border ${
                isComplete
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                  : isPartial
                  ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                  : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isComplete ? (
                      <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                    ) : isPartial ? (
                      <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                    ) : (
                      <Package className="h-5 w-5 text-gray-400" />
                    )}
                    <span className={`font-medium ${
                      isComplete
                        ? 'text-green-700 dark:text-green-300'
                        : isPartial
                        ? 'text-amber-700 dark:text-amber-300'
                        : 'text-gray-600 dark:text-gray-400'
                    }`}>
                      {isComplete ? 'Full Receipt' : isPartial ? 'Partial Receipt' : 'No Items Selected'}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {totalReceived} / {totalExpected}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">items received</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
              {receiptError && (
                <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0" />
                  <p className="text-sm text-red-700 dark:text-red-300">{receiptError}</p>
                </div>
              )}
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  onClick={handleBack}
                  className="flex-1"
                  disabled={processing}
                >
                  Back
                </Button>
                <Button
                  variant="primary"
                  onClick={handleConfirmReceipt}
                  className="flex-1"
                  loading={processing}
                  disabled={totalReceived === 0 || !receivingWorker}
                >
                  {isComplete ? 'Confirm Full Receipt' : 'Confirm Partial Receipt'}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
